/**
 * Редактор автотега: тег, который сервис вешает сам — по описанию, что видно на фото.
 *
 * Порядок работы (спека «Панель → Редактор автотега»): название и группа → «Что отбирать» по-русски
 * (английский перевод виден и правится: именно он уходит в модель) → «Показать» — предпросмотр на всех
 * библиотеках или на одной → ползунок «шире ↔ строже» двигает черту между попавшими и отсечёнными
 * локально («отберёт N из T» — по `scores`), отпустили — полосы у границы перезапрашиваются → «не то» (×)
 * на ошибочных фото → «Сохранить и разметить».
 *
 * Перевод: сервер переводит, только если `prompt_en` не прислан. Пока русский не правили после
 * последнего перевода, редактор шлёт английский как есть (в том числе правленый руками); правили —
 * пометка «перевод устарел», и «Показать» переводит заново. Отпускание ползунка шлёт тот же текст, что
 * последний «Показать», — без нового перевода.
 *
 * Новый тег: `POST /api/tags` → `POST /api/tags/{id}/autotag`; второй шаг упал — только что заведённый
 * пустой тег удаляется, ошибка остаётся на экране.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RotateCcw, X } from "lucide-react";
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";

import { Dialog, DialogFooter } from "@/components/trends/Dialog";
import { RetryImg } from "@/components/trends/PinGrid";
import { btn, btnGhost, btnPrimary, ErrorLine, input, label } from "@/components/trends/ui";
import { passCount, promptStale, reportLine, sliderRange } from "@/lib/autotag";
import { flatLibraries, libraryKeys, loadLibraries, plural } from "@/lib/library";
import { num, thumbUrl } from "@/lib/media";
import {
  createTag,
  deleteTag,
  loadTagGroups,
  loadTags,
  previewAutotag,
  renameTag,
  resetAutotag,
  saveAutotag,
  tagKeys,
  type AutotagPreview,
  type AutotagPreviewBody,
  type AutotagReportRow,
  type AutotagRow,
  type Tag,
} from "@/lib/tags";

/** Порог по умолчанию — как у сервиса (`autotag.DEFAULT_THRESHOLD`). */
const DEFAULT_THRESHOLD = 0.01;
const MAX_LINES = 5;
const MAX_LINE = 200;

type GroupChoice = number | "none" | "new";

const round3 = (v: number) => Math.round(v * 1000) / 1000;
const signed = (v: number) => `${v > 0 ? "+" : ""}${v.toFixed(3)}`;
const lines = (text: string) =>
  text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

/** Итог сохранения строкой: строка этого тега из отчёта, иначе сумма по отчёту. */
function summary(name: string, tagId: number, report: AutotagReportRow[]): string {
  const own = report.find((r) => r.tag_id === tagId);
  const sum = own ?? {
    active: report.reduce((n, r) => n + r.active, 0),
    added: report.reduce((n, r) => n + r.added, 0),
    dropped: report.reduce((n, r) => n + r.dropped, 0),
  };
  return `«${name}»: ${reportLine(sum)}`;
}

export function AutotagEditor({
  tag,
  open,
  onClose,
  onSaved,
}: {
  /** `null` — новый тег. */
  tag: Tag | null;
  open: boolean;
  onClose: () => void;
  /** Тег сохранён или сброшен: итог строкой; владелец перезапрашивает словарь и экран. */
  onSaved: (summary: string) => void;
}) {
  return (
    <Dialog open={open} wide title={tag ? `Автотег «${tag.name}»` : "Новый автотег"} onClose={onClose}>
      {open ? <EditorBody key={tag?.id ?? "new"} tag={tag} onClose={onClose} onSaved={onSaved} /> : null}
    </Dialog>
  );
}

function EditorBody({
  tag,
  onClose,
  onSaved,
}: {
  tag: Tag | null;
  onClose: () => void;
  onSaved: (summary: string) => void;
}) {
  const queryClient = useQueryClient();
  const groupsQuery = useQuery({ queryKey: tagKeys.groups, queryFn: loadTagGroups });
  const libsQuery = useQuery({ queryKey: libraryKeys.list, queryFn: loadLibraries });
  const tagsQuery = useQuery({ queryKey: tagKeys.list, queryFn: loadTags });
  const groups = groupsQuery.data?.groups ?? [];
  const libs = flatLibraries(libsQuery.data);
  const isAutoTag = tag?.prompt_en != null;

  const [name, setName] = useState(tag?.name ?? "");
  const [group, setGroup] = useState<GroupChoice>(tag?.group_id ?? "none");
  const [newGroup, setNewGroup] = useState("");
  const [ru, setRu] = useState(tag?.prompt ?? "");
  const [en, setEn] = useState(tag?.prompt_en ?? "");
  // Русский, из которого получен нынешний английский; `null` — перевода ещё не было.
  const [ruTranslated, setRuTranslated] = useState<string | null>(isAutoTag ? (tag?.prompt ?? "") : null);
  const [libraryId, setLibraryId] = useState<number | null>(null);
  const inGroup = group !== "none";
  const range = sliderRange(inGroup);
  const [threshold, setThreshold] = useState(() => Math.max(tag?.threshold ?? DEFAULT_THRESHOLD, range.min));

  const [preview, setPreview] = useState<AutotagPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const [excluded, setExcluded] = useState<Map<number, AutotagRow>>(() => new Map());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);

  // Ответы приходят не по порядку (ползунок отпускают часто): применяем только последний запрос.
  const seq = useRef(0);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  // Тело последнего «Показать» — по нему же перезапрос при отпускании ползунка.
  const lastBody = useRef<AutotagPreviewBody | null>(null);

  const stale = promptStale(ru, ruTranslated);
  const enToSend = en.trim() && !stale ? en : null;
  const ruLines = lines(ru);
  const promptProblem =
    ruLines.length === 0
      ? "Опишите, что отбирать."
      : ruLines.length > MAX_LINES
        ? `Не больше ${MAX_LINES} строк.`
        : ruLines.some((l) => l.length > MAX_LINE)
          ? `Строка длиннее ${MAX_LINE} знаков.`
          : null;
  const body: AutotagPreviewBody = {
    prompt: ru,
    prompt_en: enToSend,
    group_id: typeof group === "number" ? group : null,
    tag_id: tag?.id ?? null,
    library_id: libraryId,
    threshold,
  };
  // Предпросмотр показан по другому тексту, группе или библиотеке — подсказываем нажать «Показать».
  const outdated =
    preview !== null &&
    lastBody.current !== null &&
    (lastBody.current.prompt !== body.prompt ||
      (lastBody.current.prompt_en ?? "") !== (body.prompt_en ?? "") ||
      lastBody.current.group_id !== body.group_id ||
      lastBody.current.library_id !== body.library_id);

  const request = async (next: AutotagPreviewBody) => {
    const my = ++seq.current;
    setPreviewing(true);
    setPreviewError(null);
    try {
      const got = await previewAutotag(next);
      if (!alive.current || my !== seq.current) return;
      lastBody.current = { ...next, prompt_en: got.prompt_en };
      setPreview(got);
      // Английский приходит с сервера (перевод или нормализованный наш) — он и уходит дальше.
      setEn((cur) => (cur.trim() === (next.prompt_en ?? "").trim() || next.prompt_en === null ? got.prompt_en : cur));
      if (next.prompt_en === null) setRuTranslated(next.prompt);
      else setRuTranslated((cur) => cur ?? next.prompt);
    } catch (e) {
      if (alive.current && my === seq.current) setPreviewError(e);
    } finally {
      if (alive.current && my === seq.current) setPreviewing(false);
    }
  };
  const show = () => {
    if (!promptProblem) void request(body);
  };
  // Отпустили ползунок — полосы у границы по новому порогу, текст — как в последнем «Показать».
  const commitThreshold = () => {
    const last = lastBody.current;
    if (!last || last.threshold === threshold) return;
    void request({ ...last, threshold });
  };

  const pickGroup = (raw: string) => {
    const next: GroupChoice = raw === "none" || raw === "new" ? raw : Number(raw);
    setGroup(next);
    if (next !== "none") setThreshold((t) => Math.max(t, 0));
  };

  const toggleExcluded = (row: AutotagRow) =>
    setExcluded((cur) => {
      const next = new Map(cur);
      if (next.has(row.id)) next.delete(row.id);
      else next.set(row.id, row);
      return next;
    });

  const groupValue = group === "new" ? newGroup.trim() : group === "none" ? null : group;
  const nameText = name.trim();
  const saveProblem = !nameText
    ? "Дайте тегу название."
    : group === "new" && !newGroup.trim()
      ? "Назовите новую группу."
      : promptProblem;

  const save = async () => {
    if (saveProblem || saving) return;
    setSaving(true);
    setSaveError(null);
    const payload = {
      prompt: ru,
      prompt_en: enToSend,
      group: groupValue,
      threshold,
      exclude_ids: [...excluded.keys()],
    };
    let created: number | null = null;
    try {
      let id: number;
      if (tag) {
        id = tag.id;
        if (nameText !== tag.name) await renameTag(id, nameText);
      } else {
        const existing = (tagsQuery.data?.tags ?? []).find((t) => t.name.toLowerCase() === nameText.toLowerCase());
        if (existing)
          throw new Error(`Тег «${existing.name}» уже есть — откройте его кнопкой «Промпт…» в списке тегов.`);
        const got = await createTag(nameText);
        if (!got.created) throw new Error(`Тег «${got.name}» уже есть — откройте его «Промпт…»`);
        id = got.id;
        created = got.id;
      }
      const got = await saveAutotag(id, payload);
      void queryClient.invalidateQueries({ queryKey: tagKeys.groups });
      onSaved(summary(got.tag?.name ?? nameText, id, got.report));
    } catch (e) {
      // Новый тег без промпта не нужен: откатываем, чтобы повтор не упёрся в «уже есть».
      if (created !== null) await deleteTag(created).catch(() => undefined);
      void queryClient.invalidateQueries({ queryKey: tagKeys.list });
      if (alive.current) setSaveError(e);
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  const reset = async () => {
    if (!tag || saving) return;
    const n = tag.auto;
    const marks = `${num(n)} ${plural(n, "автометка", "автометки", "автометок")}`;
    if (!window.confirm(`Сбросить промпт тега «${tag.name}»? Тег станет ручным, автометки (${marks}) снимутся.`))
      return;
    setSaving(true);
    setSaveError(null);
    try {
      const got = await resetAutotag(tag.id);
      void queryClient.invalidateQueries({ queryKey: tagKeys.groups });
      onSaved(`«${tag.name}»: тег стал ручным, снято автометок — ${num(got.removed)}`);
    } catch (e) {
      if (alive.current) setSaveError(e);
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  // Полосы у границы: известные фото около черты делятся по ТЕКУЩЕМУ порогу — черта едет вместе с ползунком.
  const near = preview
    ? [...preview.edge.above, ...preview.edge.below].sort((a, b) => b.score - a.score || a.id - b.id)
    : [];
  const above = near.filter((r) => r.score >= threshold).reverse();
  const below = near.filter((r) => r.score < threshold);
  const passed = preview ? passCount(preview.scores, threshold) : 0;

  return (
    <div className="grid content-start gap-4 p-4" data-annot="редактор автотега" onKeyDown={keepDialogOnEsc}>
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Field title="Название">
          <input
            className={input}
            value={name}
            maxLength={60}
            placeholder="например: пышное"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field title="Группа" hint="Из группы фото получает не больше одного тега — того, что подходит лучше всех.">
          <div className="flex gap-2">
            <select
              className={`${input} min-w-0 flex-1`}
              value={String(group)}
              onChange={(e) => pickGroup(e.target.value)}
            >
              <option value="none">без группы</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
              <option value="new">+ новая группа</option>
            </select>
            {group === "new" ? (
              <input
                className={`${input} min-w-0 flex-1`}
                value={newGroup}
                maxLength={60}
                placeholder="название группы, например: Силуэт"
                autoFocus
                onChange={(e) => setNewGroup(e.target.value)}
              />
            ) : null}
          </div>
        </Field>
      </div>

      <Field
        title="Что отбирать"
        hint={
          "Опишите, что видно на фото, одной фразой — как подпись к картинке. Без „не …“ и „без …“: модель отрицаний " +
          "не понимает; для „без рукавов“ заведите второй тег группы. Несколько строк — несколько формулировок одного тега"
        }
      >
        <textarea
          className={`${input} h-auto min-h-[64px] py-1.5`}
          rows={3}
          value={ru}
          placeholder="свадебное платье с длинными рукавами"
          onChange={(e) => setRu(e.target.value)}
        />
        <div className={`text-[11.5px] tnum ${promptProblem && ru.trim() ? "text-err" : "text-fg-faint"}`}>
          строк: {ruLines.length} из {MAX_LINES}
          {promptProblem && ru.trim() ? ` — ${promptProblem}` : ""}
        </div>
      </Field>

      <Field
        title={
          <span className="inline-flex items-center gap-2">
            Текст для модели (по-английски)
            {stale ? (
              <span className="rounded-[3px] bg-warn-bg px-1.5 text-[11px] text-warn" title="Русский текст поменяли">
                перевод устарел — «Показать» переведёт заново
              </span>
            ) : null}
          </span>
        }
        hint="Появится после «Показать». Модель понимает только английский — этот текст и уходит в неё; его можно поправить."
      >
        <textarea
          className={`${input} h-auto min-h-[52px] py-1.5 ${stale ? "text-fg-faint" : ""}`}
          rows={2}
          value={en}
          placeholder="a wedding dress with long sleeves"
          onChange={(e) => setEn(e.target.value)}
        />
      </Field>

      <div className="flex flex-wrap items-end gap-2" data-annot="предпросмотр автотега">
        <Field title="Предпросмотр">
          <select
            className={input}
            value={libraryId ?? ""}
            onChange={(e) => setLibraryId(e.target.value ? Number(e.target.value) : null)}
            aria-label="Библиотека предпросмотра"
          >
            <option value="">на всех библиотеках</option>
            {libs.map((l) => (
              <option key={l.id} value={l.id}>
                на одной: {l.name} ({num(l.photos)})
              </option>
            ))}
          </select>
        </Field>
        <button type="button" className={btnPrimary} onClick={show} disabled={!!promptProblem || previewing}>
          Показать
        </button>
        {previewing ? (
          <span className="pb-2 text-[12px] text-fg-faint">{preview ? "обновляю…" : "загружаю векторы…"}</span>
        ) : outdated ? (
          <span className="pb-2 text-[12px] text-warn">условия поменялись — нажмите «Показать»</span>
        ) : null}
      </div>
      <ErrorLine error={previewError} />

      {preview ? (
        <div className="grid gap-3" data-annot="полосы предпросмотра">
          <div className="flex flex-wrap items-center gap-3 rounded-[var(--radius-ui)] border border-border px-3 py-2">
            <span className="text-[12px] text-fg-muted">шире</span>
            <input
              type="range"
              className="w-72 accent-[var(--accent)]"
              min={range.min}
              max={range.max}
              step={range.step}
              value={threshold}
              aria-label="Порог: влево — шире, вправо — строже"
              onChange={(e) => setThreshold(round3(Number(e.target.value)))}
              onPointerUp={commitThreshold}
              onKeyUp={commitThreshold}
            />
            <span className="text-[12px] text-fg-muted">строже</span>
            <span className="text-[12.5px] tnum">
              отберёт <b>{num(passed)}</b> из {num(preview.total)}
            </span>
            <span
              className="text-[11.5px] text-fg-faint tnum"
              title="Порог оценки: насколько фото ближе к описанию, чем к сопернику"
            >
              порог {signed(threshold)}
            </span>
          </div>

          <Strip title="лучшие" count={preview.top.length} hint="самые похожие на описание">
            {preview.top.map((r) => (
              <PreviewTile key={r.id} row={r} excluded={excluded.has(r.id)} onToggle={toggleExcluded} />
            ))}
          </Strip>
          <Strip title="у границы — попали" count={above.length} hint="слева — ближе к черте">
            {above.map((r) => (
              <PreviewTile key={r.id} row={r} excluded={excluded.has(r.id)} onToggle={toggleExcluded} />
            ))}
          </Strip>
          <div className="flex items-center gap-2" aria-hidden>
            <div className="h-[3px] flex-1 rounded bg-fg" />
            <span className="text-[11.5px] font-medium text-fg">граница</span>
            <div className="h-[3px] flex-1 rounded bg-fg" />
          </div>
          <Strip title="у границы — отсечены" count={below.length} hint="слева — ближе к черте">
            {below.map((r) => (
              <PreviewTile key={r.id} row={r} excluded={excluded.has(r.id)} onToggle={toggleExcluded} showRival />
            ))}
          </Strip>
        </div>
      ) : null}

      {excluded.size ? (
        <div className="grid gap-1.5" data-annot="исключено">
          <div className="text-[12px] text-fg-muted">
            исключено {num(excluded.size)} — эти фото тег не получит; клик возвращает
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[...excluded.values()].map((r) => (
              <button
                key={r.id}
                type="button"
                className="relative size-12 overflow-hidden rounded-[4px] border border-border bg-bg-subtle"
                title="Вернуть"
                onClick={() => toggleExcluded(r)}
              >
                {r.asset_path ? (
                  <RetryImg
                    src={thumbUrl(r.asset_path)}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                ) : null}
                <span className="absolute inset-0 grid place-items-center bg-black/30 text-white">
                  <RotateCcw size={14} />
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-auto grid gap-2">
        <ErrorLine error={saveError} />
      </div>
      <div className="-mx-4 -mb-4">
        <DialogFooter>
          {tag && isAutoTag ? (
            <button type="button" className={`${btnGhost} mr-auto`} onClick={reset} disabled={saving}>
              Сбросить промпт
            </button>
          ) : null}
          {saveProblem && (nameText || ru.trim()) ? (
            <span className="text-[12px] text-fg-faint">{saveProblem}</span>
          ) : null}
          <button type="button" className={btn} onClick={onClose} disabled={saving}>
            Отмена
          </button>
          <button type="button" className={btnPrimary} onClick={() => void save()} disabled={!!saveProblem || saving}>
            {saving ? "Размечаю…" : "Сохранить и разметить"}
          </button>
        </DialogFooter>
      </div>
    </div>
  );
}

/** Esc в поле не закрывает окно (там набранный промпт и исключения): уводит фокус, второй Esc — закрывает. */
function keepDialogOnEsc(e: KeyboardEvent<HTMLElement>) {
  if (e.key !== "Escape") return;
  const el = e.target as HTMLElement;
  if (!["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)) return;
  e.preventDefault();
  e.stopPropagation();
  el.blur();
}

function Field({ title, hint, children }: { title: ReactNode; hint?: string; children: ReactNode }) {
  return (
    <label className="grid min-w-0 content-start gap-1">
      <span className={label}>{title}</span>
      {children}
      {hint ? <span className="text-[11.5px] leading-snug text-fg-faint">{hint}</span> : null}
    </label>
  );
}

function Strip({ title, count, hint, children }: { title: string; count: number; hint: string; children: ReactNode }) {
  return (
    <section className="grid gap-1">
      <div className="flex items-baseline gap-2 text-[12.5px]">
        <span className="font-medium text-fg">{title}</span>
        <span className="tnum text-fg-faint">{num(count)}</span>
        <span className="text-[11.5px] text-fg-faint">{hint}</span>
      </div>
      {count ? (
        <div className="flex gap-2 overflow-x-auto pb-2">{children}</div>
      ) : (
        <div className="py-3 text-[12px] text-fg-faint">нет фото</div>
      )}
    </section>
  );
}

function PreviewTile({
  row,
  excluded,
  onToggle,
  showRival = false,
}: {
  row: AutotagRow;
  excluded: boolean;
  onToggle: (row: AutotagRow) => void;
  showRival?: boolean;
}) {
  return (
    <figure className="grid w-[120px] flex-none content-start gap-0.5">
      <div className="relative h-[160px] overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-subtle">
        <a
          href={`/lib/ref/${row.id}`}
          target="_blank"
          rel="noreferrer"
          title="Открыть карточку референса"
          className={`absolute inset-0 ${excluded ? "opacity-30" : ""}`}
        >
          {row.asset_path ? (
            <RetryImg src={thumbUrl(row.asset_path)} alt="" className="absolute inset-0 h-full w-full object-cover" />
          ) : null}
        </a>
        <button
          type="button"
          className={`absolute top-1 right-1 inline-flex h-5 items-center gap-0.5 rounded-[4px] px-1 text-[11px] text-white ${
            excluded ? "bg-accent" : "bg-black/60 hover:bg-black/80"
          }`}
          title={excluded ? "Вернуть: тег решит по оценке" : "Не то: это фото тег не получит"}
          aria-pressed={excluded}
          onClick={() => onToggle(row)}
        >
          {excluded ? <RotateCcw size={11} /> : <X size={11} />}
          {excluded ? "вернуть" : "не то"}
        </button>
        <span className="absolute bottom-1 left-1 rounded-[3px] bg-black/60 px-1 text-[10.5px] leading-[15px] text-white tnum">
          {signed(row.score)}
        </span>
      </div>
      {showRival && row.rival !== null ? (
        <figcaption className="truncate text-[11px] text-fg-faint" title={`ушло в «${row.rival}»`}>
          ушло в «{row.rival}»
        </figcaption>
      ) : null}
    </figure>
  );
}
